import * as React from 'react';
import { oc } from 'ts-optchain';
import _ from 'lodash';

import { DisplayMoney } from '@tock/money';
import { Typography } from 'omakase';
import { core } from '@tock/coretypes';
import { usePaidQuickCheckout } from 'components/business/checkout/checkoutHooks';
import Anchor from 'components/Anchor';
import Button from '@tock/shared/components/buttons/DeprecatedButton';
import Field from '@tock/shared/components/forms/Field';
import Fields from '@tock/shared/components/forms/Fields';
import Fieldset from '@tock/shared/components/forms/Fieldset';
import Form from '@tock/shared/components/forms/Form';
import TextInput from '@tock/shared/components/inputs/DeprecatedTextInput';

interface Props {
  onApplyDiscountCode(discountCode: string): void;
  onRemoveDiscountCode(): void;
  purchaseOrder?: core.PurchaseOrder;
  ticketTypeDiscountCode?: core.TicketTypePromoCode;
}

export const DiscountCodeForm: React.FunctionComponent<Props> = (props: Props) => {
  const { purchaseOrder, onApplyDiscountCode, onRemoveDiscountCode, ticketTypeDiscountCode } =
    props;
  const [value, setValue] = React.useState('');

  const shouldPromptLogin = usePaidQuickCheckout(purchaseOrder || null);
  const getDiscountPromoCode = (props: Props) => {
    return _.find(oc(purchaseOrder).discount([]), (discount) => !!discount.promoCode);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue(_.toUpper(e.currentTarget.value));
  };

  const handleApply = (e: React.FormEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();

    onApplyDiscountCode(value);
  };

  const field = {
    value: value,
    onChange: handleChange,
    onBlur: _.noop,
  };

  const discountPromoCode = getDiscountPromoCode(props);

  return (
    <Form modifierClassName="Receipt-discountCodeForm" onSubmit={handleApply}>
      {!discountPromoCode && !ticketTypeDiscountCode && (
        <div className="Receipt-discountCodeForm--header">
          <Typography color="inherit" component="span" variant="body02Strong">
            Have a discount code?
          </Typography>
        </div>
      )}

      <Fieldset>
        <Fields>
          {ticketTypeDiscountCode && (
            <Field>
              <span className="Receipt-discountCodeApplied">
                <Typography color="inherit" component="span" variant="body02">
                  Discount applied, {oc(ticketTypeDiscountCode).promoCode()} (
                  {oc(ticketTypeDiscountCode).percentOff() ? (
                    <span>{oc(ticketTypeDiscountCode).percentOff(0)}% off</span>
                  ) : (
                    <span>
                      <DisplayMoney amount={oc(ticketTypeDiscountCode).amountOffCents(0)} /> off
                    </span>
                  )}
                  {oc(ticketTypeDiscountCode).discountScope() === 'PER_COVER' && ' per person'})
                </Typography>
              </span>
              <Anchor className="Receipt-discountCodeLink" onClick={onRemoveDiscountCode}>
                <Typography color="inherit" component="span" variant="body02">
                  Remove
                </Typography>
              </Anchor>
            </Field>
          )}
          {!ticketTypeDiscountCode && discountPromoCode && (
            <Field>
              <span className="Receipt-discountCodeApplied">
                <Typography color="inherit" component="span" variant="body02">
                  Discount applied, {discountPromoCode.promoCode}{' '}
                  <DisplayMoney amount={discountPromoCode.amountCents} /> off
                </Typography>
              </span>
            </Field>
          )}
          {!shouldPromptLogin && !discountPromoCode && !ticketTypeDiscountCode && (
            <>
              <Field>
                <TextInput field={field} placeholder="Enter your discount code" type="text" />
              </Field>
              <Field className="Receipt-discountCodeButton">
                <Button
                  size="medium"
                  disabled={!value}
                  type="button"
                  onClick={handleApply}
                  variant="secondary"
                >
                  Apply
                </Button>
              </Field>
            </>
          )}
          {shouldPromptLogin && (
            <span className="Receipt-discountCodeLoginRequired">
              <Typography color="inherit" component="span" variant="body02">
                You must log in to enter a discount code
              </Typography>
            </span>
          )}
        </Fields>
      </Fieldset>
    </Form>
  );
};
